import React, { useMemo } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import { useAccountGroups } from 'api/hooks/use-account-groups'
import { useActiveAccountGrouping } from 'api/hooks/use-account-groupings'
import { groupLabel } from 'account-groups/scopes'
import MultiSelectFilterPopover from './MultiSelectFilterPopover'

// Filter popover for the synthetic account-group columns (Object Group, Account
// Group, …). Options are the municipality's group labels for this column's scope
// — unlike other multiselects, they come from the account_groups API rather than
// the table_columns filter_options payload, because groups are user-managed and
// change without a column reseed.
//
// Values are group *ids*, not names: names are user-editable, so filtering by
// name would break every saved View on a rename. The server expands the ids to
// member ids (Filterers::Transactions#filter_account_groups).
//
// Scoped to the grouping this surface reports under: the column buckets by exactly one, so
// offering a rollup from another grouping would offer a filter matching nothing it shows.
const AccountGroupFilterPopover = ({
  column,
  params,
  setParams,
  onClose,
  dashboardId = null,
  canEdit = true,
  ...props
}) => {
  const grouping = useActiveAccountGrouping(dashboardId)
  const { data: groups, isLoading } = useAccountGroups({
    groupingId: grouping?.id,
    options: { enabled: Boolean(grouping?.id) }
  })

  const accessorKey = column.accessor_key
  const appliedValues = useMemo(() => {
    const applied = params?.[accessorKey]
    if (!applied) return []
    return Array.isArray(applied) ? applied : [applied]
  }, [params, accessorKey])

  // 'object_group' -> 'object', 'account_group' -> 'account'. Matches
  // TableColumn#group_scope and AccountGroup#scope_key.
  const scope = useMemo(
    () => (column?.database_column_name || '').replace(/_group$/, ''),
    [column?.database_column_name]
  )

  const options = useMemo(
    () =>
      (groups || [])
        .filter((group) => group.scope_key === scope)
        // Code and name rejoined, so an option reads the same as the value it
        // filters the column down to (AccountGroups::SqlBuilder).
        .map((group) => ({ value: group.id, label: groupLabel(group) })),
    [groups, scope]
  )

  // A saved filter can name a rollup from a grouping this surface no longer reports under.
  // Those ids have no checkbox below, so without this the filter is applied, invisible and
  // matching nothing, with no way to clear it.
  const orphanedValues = useMemo(
    () => appliedValues.filter((value) => !options.some((option) => option.value === value)),
    [appliedValues, options]
  )

  const clearOrphaned = () =>
    setParams((p) => ({
      ...p,
      [accessorKey]: appliedValues.filter((value) => !orphanedValues.includes(value))
    }))

  // Unlike sibling popovers, the options are fetched on open rather than riding
  // along with table_columns. Rendering the multiselect before they arrive would
  // expose a "Select All" over an empty list, which silently clears an applied
  // filter, so hold the popover until the groups are in hand.
  if (isLoading) {
    return (
      <div className="p-3 text-center">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  // With no groups for this scope, AccountGroups::SqlBuilder falls the column back
  // to the raw label (`ELSE <segment>_codes.name`), so the column is populated —
  // with each code's own name — while there is nothing to filter by. Saying only
  // "none exist" reads as a bug against a column full of values, so say both.
  // A saved value outlives its groups, and the checkbox list that would clear it isn't rendered here.
  if (!options.length) {
    return (
      <div className="p-3 text-muted small">
        No rollups exist for this column yet, so it shows each code&rsquo;s own
        name — the same values as the ungrouped column. Create one on the
        Account&nbsp;Groups page to bucket them.
        {appliedValues.length > 0 && (
          <div className="mt-3 d-flex justify-content-between align-items-center gap-2">
            <span>
              A filter is still saved here, and it matches no rollup.
            </span>
            <Button
              variant="light-primary"
              size="sm"
              className="flex-shrink-0"
              disabled={!canEdit}
              onClick={() => {
                setParams((p) => ({ ...p, [accessorKey]: [], [`${accessorKey}Mode`]: 'include' }))
                onClose()
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {orphanedValues.length > 0 && (
        <div className="px-3 pt-3 text-muted small d-flex justify-content-between align-items-center gap-2">
          <span>
            {orphanedValues.length === 1
              ? 'A saved filter here names a rollup from another account grouping, so it matches nothing in this data.'
              : `${orphanedValues.length} saved filters here name rollups from another account grouping, so they match nothing in this data.`}
          </span>
          <Button
            variant="light-primary"
            size="sm"
            className="flex-shrink-0"
            disabled={!canEdit}
            onClick={clearOrphaned}
          >
            {/* Not "Clear": the multiselect below has its own Clear, which drops every
                selection. This one drops only the unreachable ids, and two buttons reading the
                same word in one popover would be a coin toss. */}
            Remove
          </Button>
        </div>
      )}
      <MultiSelectFilterPopover
        column={column}
        params={params}
        setParams={setParams}
        onClose={onClose}
        canEdit={canEdit}
        options={options}
        {...props}
      />
    </>
  )
}

export default AccountGroupFilterPopover
